import { Link, type LinkProps } from 'react-router-dom';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger' | 'subtle';
type Size = 'sm' | 'md' | 'lg';

const variants: Record<Variant, string> = {
  primary:
    'bg-primary text-primary-ink hover:bg-primary-hover shadow-soft active:translate-y-px',
  secondary:
    'bg-ink text-bg hover:bg-ink/90 active:translate-y-px',
  ghost:
    'text-ink hover:bg-muted',
  outline:
    'border border-border bg-surface text-ink hover:bg-elevated',
  danger:
    'bg-danger text-white hover:bg-danger/90 active:translate-y-px',
  subtle:
    'bg-primary-soft text-primary hover:bg-primary-soft/70',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2.5',
};

export interface ButtonLinkProps extends Omit<LinkProps, 'children'> {
  variant?: Variant;
  size?: Size;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
  children: ReactNode;
}

export function ButtonLink({
  className,
  variant = 'primary',
  size = 'md',
  iconLeft,
  iconRight,
  fullWidth,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-150',
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {iconLeft && <span className="shrink-0">{iconLeft}</span>}
      {children}
      {iconRight && <span className="shrink-0">{iconRight}</span>}
    </Link>
  );
}
